import React, { useContext, useEffect, useState, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { HotelContext } from "context/HotelContext";
import DeclinedServiceCard from "component/DeclinedServiceCard";
import Translation from "utils/Translation";
import logoRAS from "../img/Logo_RAS.png";
import calendarPng from "../img/Calendrier.png";
import BreadCrumb from "component/Breadcrumb";

const DeclinedServices = () => {
  const { hId, lang } = useContext(HotelContext);
  const keycloak = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(null); // "YYYY-MM-DD"
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const dateInputRef = useRef(null);

  const headers = {
    Authorization: "Bearer " + (keycloak.user?.access_token || ""),
    Accept: "application/json",
    "Content-Type": "application/json",
    "x-front-token": "Bs#PSQyhy)!y9Ti{",
  };

  useEffect(() => {
    if (hId && keycloak.isAuthenticated) {
      fetchDays();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hId, keycloak.isAuthenticated]);

  const fetchDays = async () => {
    try {
      // Appel informatif (non bloquant) pour peupler le cache si besoin
      fetch(`/api/hotels/${hId}/luniclean/days`, { method: "GET", headers }).catch(() => {});
      // Toujours démarrer sur la date du jour
      const initialDate = toISODate(new Date());
      setCurrentDate(initialDate);
      await fetchDeclines(initialDate); // laisser fetchDeclines gérer le loading
    } catch {
      const today = toISODate(new Date());
      setCurrentDate(today);
      setRooms([]);
    }
  };

  const fetchDeclines = async (datePlus1) => {
    setLoading(true);
    try {
      const urlLC = `/api/hotels/${hId}/luniclean/declines?datePlus1=${datePlus1}&lang=${lang || 'en'}`;
      const urlYNM = `/api/hotels/${hId}/yesnomail/declines?datePlus1=${datePlus1}&lang=${lang || 'en'}`;
      const [resLC, resYNM] = await Promise.all([
        fetch(urlLC, { method: "GET", headers }),
        fetch(urlYNM, { method: "GET", headers }),
      ]);
      const [dataLC, dataYNM] = await Promise.all([resLC.json(), resYNM.json()]);

      const lc = dataLC?.success === "OK" && Array.isArray(dataLC.rooms) ? dataLC.rooms : [];
      const ynmRows = dataYNM?.success === "OK" && Array.isArray(dataYNM.rows) ? dataYNM.rows : [];

      const normalizedLC = lc.map((r) => ({
        roomNumber: r.roomNumber || r.rRoomNumber || r.rRoomNum,
        refusedAt: r.refusedAt,
        services:
          Array.isArray(r.services) && r.services.length
            ? r.services.map((s) => ({ text: s, refusedAt: r.refusedAt }))
            : [{ text: r.actNameTranslated || r.actName || "LUNICLEAN", refusedAt: r.refusedAt }],
      }));

      const normalizedYNM = ynmRows
        .map((row) => {
          const refusedAt = row.refusedAt || row.updatedAt;
          // Utiliser la traduction si disponible, sinon actName
          const actionName = row.actNameTranslated || row.actName || "YESNOMAIL";
          return {
            roomNumber: row.rRoomNumber || row.rRoomNum || row.roomNumber,
            refusedAt,
            services: [{ text: String(actionName), refusedAt, source: "YESNOMAIL" }],
          };
        });

      const byRoom = new Map();
      for (const item of [...normalizedLC, ...normalizedYNM]) {
        if (!item.roomNumber) continue;
        const prev = byRoom.get(item.roomNumber);
        if (!prev) {
          byRoom.set(item.roomNumber, { ...item });
        } else {
          const merged = {
            roomNumber: item.roomNumber,
            refusedAt: new Date(item.refusedAt || 0) > new Date(prev.refusedAt || 0) ? item.refusedAt : prev.refusedAt,
            services: [ ...(prev.services || []), ...(item.services || []) ],
          };
          byRoom.set(item.roomNumber, merged);
        }
      }

      setRooms(Array.from(byRoom.values()));
    } catch (e) {
      setRooms([]);
    } finally {
      setLoading(false);
    }
  };

  const goPrev = () => {
    if (!currentDate) return;
    const d = new Date(currentDate);
    d.setDate(d.getDate() - 1);
    const iso = toISODate(d);
    setCurrentDate(iso);
    fetchDeclines(iso);
  };
  const goNext = () => {
    if (!currentDate) return;
    const d = new Date(currentDate);
    d.setDate(d.getDate() + 1);
    const iso = toISODate(d);
    setCurrentDate(iso);
    fetchDeclines(iso);
  };

  const onCalendarClick = () => {
    setIsDatePickerOpen((v) => !v);
    // Tente d'ouvrir immédiatement le sélecteur si supporté
    setTimeout(() => {
      if (dateInputRef.current) {
        if (typeof dateInputRef.current.showPicker === "function") {
          try { dateInputRef.current.showPicker(); } catch (e) { /* noop */ }
        }
        dateInputRef.current.focus();
      }
    }, 0);
  };

  const onDatePicked = (e) => {
    const val = e.target.value;
    if (!val) return;
    setCurrentDate(val);
    fetchDeclines(val);
    setIsDatePickerOpen(false);
  };

  return (
    <div className="green-miles-actions-container">
      <BreadCrumb text="Declined_services_title" />
      <h1 className="h1-page">
        <Translation tag="Declined_services_title" />
      </h1>
      <p className="cleaning-description">
        <em>
          <Translation tag="Declined_services_line2" />
        </em>
      </p>

      {/* Barre de date : flèche gauche — [ calendrier + date ] — flèche droite */}
      <div className="declined-datebar">
        <button
          className="nav-arrow left"
          onClick={goPrev}
          // navigation toujours possible vers les jours précédents
          disabled={false}
          aria-label="Jour précédent"
        >
          <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
            <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className="date-center" style={{ position: "relative" }}>
          <img
            src={calendarPng}
            className="cal-ic cal-img"
            alt=""
            onClick={onCalendarClick}
            style={{ cursor: "pointer" }}
            aria-label="Choisir une date"
            title="Choisir une date"
          />
          <span className="date-text" onClick={onCalendarClick} style={{ cursor: "pointer" }}>{formatDate(currentDate, lang)}</span>
          {isDatePickerOpen && (
            <input
              ref={dateInputRef}
              type="date"
              value={currentDate || ""}
              onChange={onDatePicked}
              style={{ position: "absolute", right: "110%", top: "50%", transform: "translateY(-50%)", zIndex: 10 }}
            />
          )}
        </div>

        <button
          className="nav-arrow right"
          onClick={goNext}
          // navigation toujours possible vers les jours suivants
          disabled={false}
          aria-label="Jour suivant"
        >
          <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {loading ? (
        <p>Chargement…</p>
      ) : rooms.length === 0 ? (
        <div className="declined-empty">
          <div className="declined-empty-content">
            <img src={logoRAS} alt="RAS" />
            <div className="declined-empty-title"><Translation tag="Declined_services_empty_title" /></div>
            <div className="declined-empty-desc"><Translation tag="Declined_services_empty_desc" /></div>
          </div>
        </div>
      ) : (
        <div className="declined-list">
          {rooms.map((r) => (
            <DeclinedServiceCard
              key={r.rId}
              number={r.roomNumber || r.rRoomNumber}
              refusedAt={r.refusedAt}
              services={Array.isArray(r.services) ? r.services : ["LUNICLEAN"]}
            />
          ))}
        </div>
      )}
    </div>
  );
};

function formatDate(iso, lang = "fr") {
  if (!iso) return "—";
  const d = new Date(iso);
  const locale = lang === "en" ? "en-US" : "fr-FR";
  
  if (lang === "en") {
    const weekday = d.toLocaleDateString(locale, { weekday: "long" });
    const day = d.toLocaleDateString(locale, { day: "numeric" });
    const month = d.toLocaleDateString(locale, { month: "long" });
    const year = d.getFullYear();
    const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
    return `${cap(weekday)} ${day} ${cap(month)} ${year}`;
  } else {
    const weekday = d.toLocaleDateString(locale, { weekday: "long" });
    const day = d.toLocaleDateString(locale, { day: "2-digit" });
    const months = [
      "Janvier","Février","Mars","Avril","Mai","Juin",
      "Juillet","Août","Septembre","Octobre","Novembre","Décembre"
    ];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
    return `${cap(weekday)} ${day} ${month} ${year}`;
  }
}

function toISODate(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default DeclinedServices;
